import jsPDF from 'jspdf';

// Shared jsPDF helpers used by the ad "Creative Summary" PDF (generateAdPdf)
// and the script "Creator Brief" PDF (generateScriptPdf).

// Pretty-print an enum-ish value: snake_case → Title Case. '-' for empty.
export function formatEnum(val: string | null | undefined): string {
  if (!val) return '-';
  return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// jsPDF's standard fonts (Helvetica / WinAnsi) can't render emoji or other
// astral/symbol glyphs — they come out as garbage (e.g. "Ø=ÞL"). Strip them so
// text renders cleanly. Keeps Latin-1 + General Punctuation (em dash, curly
// quotes, bullet), which WinAnsi handles fine.
export function sanitizePdfText(text: string): string {
  return text.replace(
    /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E000}-\u{F8FF}]/gu,
    '',
  );
}

// Wrap text to a max width using jsPDF's measurer (sanitized for the PDF fonts).
export function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  return doc.splitTextToSize(sanitizePdfText(text), maxWidth);
}

// Fetch an image, downscale it (long side capped at `maxDim`) and re-encode it
// to a compressed baseline JPEG for embedding in a PDF. Keyframes are only ever
// shown small in the brief, so we trade fidelity for a much smaller PDF + far
// less embedded data. Re-encoding via canvas also sidesteps jsPDF's flaky
// decoding of some source JPEGs (progressive / CMYK). Returns the data URL +
// the downscaled dimensions (for aspect-ratio layout). (Requires CORS on the
// image host.) Returns null on any failure so callers can skip silently.
export async function fetchImageForPdf(
  url: string,
  maxDim = 500,
  quality = 0.72,
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    return { dataUrl: canvas.toDataURL('image/jpeg', quality), width: w, height: h };
  } catch {
    return null;
  }
}

// Map a data-URL mime to a jsPDF addImage format string.
export function imageFormatFromDataUrl(dataUrl: string): 'JPEG' | 'PNG' | 'WEBP' {
  const m = /^data:image\/(\w+)/i.exec(dataUrl);
  const fmt = (m && m[1] ? m[1] : 'jpeg').toLowerCase();
  if (fmt === 'png') return 'PNG';
  if (fmt === 'webp') return 'WEBP';
  return 'JPEG';
}

// Fetch an image URL and return it as a base64 data URL for doc.addImage.
// Returns null on any failure (CORS, 404, …) so callers can skip silently.
export async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
